import { useState, useCallback, useMemo, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Fade, Typography, makeStyles } from '@material-ui/core';
import { PRESETS } from 'constants/presets';
import PresetDropdown from '../../shared/presetDropdown';
import AlertDialog from '../../shared/alertDialog';
import PageContainer from '../../shared/pageContainer';
import SelectField from '../../shared/selectField';
import CustomDateRange from '../../shared/customDateRange';
import SectionTitle from '../../shared/sectionTitle';
import MultipartPeoplePicker from '../../shared/multipartPeoplePicker';
import AttributeInfoBar from '../../shared/attributeInfoBar';
import ReportDetailsTable from '../../shared/reportDetailsTable';
import DeepDiveReport from '../../shared/deepDiveReport';
import NotificationCard from '../../shared/notificationCard';
import ReadLessMore from '../../shared/readLessMore';
import {
  API_USERS_BASIC,
  API_REPORT_ATTRIBUTE_ANSWERS,
  API_REPORT_ATTRIBUTE_REPLIES,
  API_REPORT_ATTRIBUTE_FEEDBACK,
  API_REPORT_ATTRIBUTE_TIMELINE,
  API_REPORT_ATTRIBUTE_SCORE,
  api_attribute,
} from '../../../constants/apiRoutes';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { SYSTEM_ROLES, SYSTEM_ROLE_OPTIONS } from '../../../constants/roles';
import {
  SURVEY_TYPES,
  INITIAL_SURVEY_TYPE_FILTER_VALUE,
} from '../../../constants/survey';
import { APP_PAGES, PARAMS } from '../../../constants/pages';
import { sticky } from '../../../constants/helperCssRules';
import { surveysSelector } from '../../../store/selectors/surveysSelector';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { jobTitlesSelector } from '../../../store/selectors/jobTitlesSelector';
import { levelsAndTracksSelector } from '../../../store/selectors/levelsAndTracksSelector';
import { divisionsAndDepartmentsSelector } from '../../../store/selectors/divisionsAndDepartmentsSelector';
import {
  getAttributesWithQuestions,
  clearAttributesWithQuestions,
} from '../../../store/modules/surveys';
import { getJobTitles, clearJobTitles } from '../../../store/modules/jobTitles';
import {
  getLevelsAndTracks,
  clearLevelsAndTracks,
} from '../../../store/modules/levelsAndTracks';
import {
  getTagsCategories,
  clearTagsCategories,
} from '../../../store/modules/tags';
import { getAllUsers, clearAllUsers } from '../../../store/modules/people';
import {
  addNewPreset,
  deleteSelectedPreset,
  editPresetActivity,
} from '../../../store/modules/organizationSettings';
import {
  getDivisionsAndDepartments,
  clearDivisionsAndDepartments,
} from '../../../store/modules/divisionsAndDepartments';
import {
  getEmployees,
  isArrayEmpty,
  getObjectToNumberArray,
  isObjectEmpty,
  isValueInList,
  areArraysContentsEqual,
  getItemById,
} from '../../../utility/helpers';
import {
  NO_ACTIVE_PRESET,
  getActivePresetId,
  getActivePresetName,
  getFilteredPresets,
  getPlaceholderName,
  handleHasMaxPresets,
  handlePresetName,
  presetPeriod,
  getSelectedSurveyType,
} from '../../../utility/presets';
import http from '../../../utility/http';
import { preparePeopleFilters } from '../../../utility/people';
import { prepareRoles } from '../../../utility/roles';
import {
  prepareSurveyTypeFilterOptions,
  prepareAnswerGridResults,
  prepareTextReplyResults,
  transformReviewData,
} from '../../../utility/reportUtils';
import { useTranslations } from '../../../utility/useTranslations';
import {
  hasAuthPermissionsCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  parseDuplicateParameters,
  showSuccessMessage,
} from '../../../utility/uiUtils';
import { getStartEndOfDayUtc } from '../../../utility/dateUtils';
import { prepareSurveyStatuses } from '../../../utility/survey';
import { getLast12Months } from '../../shared/customDateRange/config';
import { ATTRIBUTE_TYPES_MAP } from '../attributesPage/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  pageDescription: {
    ...sticky(primary.white, 105),
    width: '100%',
    padding: spacing(8, 0, 8, 0),
  },
  filtersWrapper: {
    borderBottom: `1px solid ${primary.bluish7}`,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0,350px))',
    gridColumnGap: 16,
    marginBottom: spacing(8),
    paddingBottom: spacing(6),
    '&:last-of-type': {
      marginBottom: spacing(0),
    },
  },
  filtersUsersWrapper: {
    display: 'block',
  },
  usersSectionDescription: {
    margin: spacing(1, 0, 6),
  },
  topSpacing: {
    marginTop: spacing(6),
  },
  attributeInfoNoScore: {
    padding: spacing(3, 4, 3, 0),
  },
}));

const ATTRIBUTE_PRESET_ID = PRESETS.ATTRIBUTE;
const DROPDOWN_WIDTH = '350px';
const INPUT_WIDTH = 350;

const AttributeReportPage = ({
  navigate,
  auth,
  accessControl,
  presets,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.ATTRIBUTE_REPORT);

  const [isDeletePresetDialogOpened, setIsDeletePresetDialogOpened] =
    useState(false);

  const dispatch = useDispatch();
  const location = useLocation();
  const [tempState, setTempState] = useState(location?.state);
  const attributeState = tempState?.attribute;
  const getInitialPreset = () => {
    const attributeReportPagePresets = attributeState
      ? null
      : presets[ATTRIBUTE_PRESET_ID];
    const existsPresetsArray =
      attributeReportPagePresets && !isArrayEmpty(attributeReportPagePresets);
    const tempInitialPreset = existsPresetsArray
      ? getItemById(
          attributeReportPagePresets,
          presets.active[ATTRIBUTE_PRESET_ID]
        )
      : null;
    return tempInitialPreset;
  };
  const [initialPreset, setInitialPreset] = useState(getInitialPreset());
  const [selectedPreset, setSelectedPreset] = useState(initialPreset);
  const [defaultPlaceholder, setDefaultPlaceholder] = useState(false);
  const [presetPlaceholderName, setPresetPlaceholderName] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [isReadMore, setIsReadMore] = useState(true);
  const [selectedSurveyType, setSelectedSurveyType] = useState(
    INITIAL_SURVEY_TYPE_FILTER_VALUE
  );
  const [currentAttributeData, setCurrentAttributeData] = useState(null);
  const [period, setPeriod] = useState(getLast12Months());
  const [results, setResults] = useState({});
  const [attributeScore, setAttributeScore] = useState(null);
  const [openEndedQuestionId, setOpenEndedQuestionId] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [surveyReference, setSurveyReference] = useState([]);

  const { attributesWithQuestions } = useSelector(surveysSelector);
  const { allUsers } = useSelector(peopleSelector);
  const { jobTitles } = useSelector(jobTitlesSelector);
  const { tracks } = useSelector(levelsAndTracksSelector);
  const { divisions, departments } = useSelector(
    divisionsAndDepartmentsSelector
  );
  const { categories } = useSelector(tagsSelector);

  const isOpenEndedAttribute = currentAttributeData?.text_reply_questions;
  const surveyTypeOptions = prepareSurveyTypeFilterOptions(
    translations.surveyTypeFilterOptions,
    true
  );
  const surveyStatuses = prepareSurveyStatuses(translations.surveyStatuses);
  const detailsTableColumns = [
    { label: translations.review.details.date },
    { label: translations.review.details.commentText },
    { label: translations.review.details.status },
  ];

  const allEmployees = useMemo(() => getEmployees(allUsers), [allUsers]);
  const [reviewedUsers, setReviewedUsers] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [attribute, setAttribute] = useState(attributeState || {});

  const getInitialAttribute = () => {
    const tempValue =
      initialPreset && !isArrayEmpty(attributesWithQuestions)
        ? getItemById(attributesWithQuestions, initialPreset?.data?.attribute)
        : attribute;
    setAttribute(tempValue);
    return tempValue;
  };
  const initialAttribute = useMemo(
    () => getInitialAttribute(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialPreset, attribute, attributesWithQuestions]
  );
  const isSelfReport = areArraysContentsEqual(selectedSurveyType.value, [
    SURVEY_TYPES.SELF,
  ]);
  const isPulseReport = areArraysContentsEqual(selectedSurveyType.value, [
    SURVEY_TYPES.PULSE,
  ]);
  const presetState = {
    defaultPlaceholder,
    presetPlaceholderName,
    isLoading,
  };

  const activePresetId = attributeState
    ? NO_ACTIVE_PRESET
    : getActivePresetId(presets, ATTRIBUTE_PRESET_ID);
  const message =
    activePresetId !== NO_ACTIVE_PRESET ? translations.presets.used : '';
  const [alreadySavedMessage, setAlreadySavedMessage] = useState(message);
  const filteredPresets = getFilteredPresets(presets, ATTRIBUTE_PRESET_ID);
  const presetName = getActivePresetName(filteredPresets, activePresetId);
  const handledPresetName = handlePresetName(
    filteredPresets,
    activePresetId,
    presetName,
    presetState,
    translations.presets.startSearch
  );
  const placeholderName = getPlaceholderName(
    handledPresetName,
    presetName,
    translations.presets.startSearch
  );

  const reviewedUsersFilters = useMemo(
    () =>
      !isLoading
        ? preparePeopleFilters(
            translations,
            jobTitles,
            tracks,
            divisions,
            departments,
            prepareRoles(translations.systemRoles, SYSTEM_ROLE_OPTIONS, true),
            [],
            categories,
            true
          )
        : [],
    [
      translations,
      isLoading,
      jobTitles,
      tracks,
      categories,
      divisions,
      departments,
    ]
  );
  const reviewersFilters = useMemo(
    () =>
      !isLoading
        ? preparePeopleFilters(
            translations,
            jobTitles,
            tracks,
            divisions,
            departments,
            prepareRoles(translations.systemRoles, SYSTEM_ROLE_OPTIONS, true),
            [],
            categories,
            true
          )
        : [],
    [
      translations,
      isLoading,
      jobTitles,
      tracks,
      categories,
      divisions,
      departments,
    ]
  );

  const handleResetPreset = useCallback(() => {
    editPresetActivity(dispatch, -1, ATTRIBUTE_PRESET_ID, false);
    setSelectedPreset(undefined);
    setInitialPreset(undefined);
    setAlreadySavedMessage('');
  }, [dispatch]);

  const areFilterAndPresetEqual = (selectedData, presetData) => {
    return (
      areArraysContentsEqual(
        selectedData?.subject || [],
        presetData?.subject || []
      ) &&
      areArraysContentsEqual(
        selectedData?.reviewer || [],
        presetData?.reviewer || []
      ) &&
      selectedData?.from_date === presetData.from_date &&
      selectedData?.to_date === presetData.to_date &&
      areArraysContentsEqual(
        selectedData?.survey_type,
        presetData.survey_type
      ) &&
      selectedData?.attribute === presetData?.attribute
    );
  };

  const checkValueInPresets = selectedData => {
    const attributePresets = presets[ATTRIBUTE_PRESET_ID];
    setTempState(null);

    if (attributePresets && !isArrayEmpty(attributePresets)) {
      const activePreset = attributePresets.find(preset => {
        const presetData = preset?.data;
        return areFilterAndPresetEqual(selectedData, presetData);
      });
      if (activePreset) {
        editPresetActivity(
          dispatch,
          activePreset.id,
          ATTRIBUTE_PRESET_ID,
          true
        );
        setInitialPreset(undefined);
        setAlreadySavedMessage(translations.presets.used);
      } else {
        handleResetPreset();
      }
    }
  };

  const reportParams = useMemo(() => {
    const { ATTRIBUTE, SURVEY_TYPE, START, END, START_AT, END_AT } = PARAMS;
    const subject = getObjectToNumberArray(reviewedUsers);
    const reviewer = getObjectToNumberArray(reviewers);

    return {
      [ATTRIBUTE]: initialAttribute?.id,
      [SURVEY_TYPE]: selectedSurveyType.value,
      [START_AT]: initialPreset
        ? initialPreset?.data?.from_date
        : getStartEndOfDayUtc(period[START]),
      [END_AT]: initialPreset
        ? initialPreset?.data?.to_date
        : getStartEndOfDayUtc(period[END], true),
      ...(isPulseReport ? {} : { subject }),
      ...(isSelfReport ? {} : { reviewer }),
    };
  }, [
    initialAttribute,
    period,
    selectedSurveyType,
    reviewers,
    reviewedUsers,
    initialPreset,
    isSelfReport,
    isPulseReport,
  ]);

  const goToUserProfilePage = userId => navigate(`/people/${userId}`);

  const prepareSurveyLinkPath = survey =>
    survey?.cycle
      ? `/cycles/${survey.cycle}/reviews/${survey.id}/report`
      : `/surveys/${survey.id}/report`;

  const hasRedirectLinkCheck = survey => {
    const [canViewAllSurveys, canViewSurveysCreated, canViewCycles] =
      hasAuthPermissionsCheck(
        [
          PERMISSION_CODES.SURVEYS_VIEW_ALL,
          PERMISSION_CODES.SURVEYS_VIEW_CREATED,
          PERMISSION_CODES.CYCLES_VIEW,
        ],
        accessControl
      );

    if (survey?.cycle) {
      if (canViewCycles) {
        if (canViewAllSurveys) return true;

        if (
          canViewSurveysCreated &&
          canViewCycles &&
          survey?.creator === auth.id
        )
          return true;

        return false;
      }
      return false;
    }

    return (
      canViewAllSurveys ||
      (canViewSurveysCreated && survey?.creator === auth.id)
    );
  };
  const canAccessUserProfileCheck = user =>
    isUserProfileAccessibleCheck(accessControl, auth.id, user.id);

  const handleInitData = data => {
    const [, allUsersResults, , , ,] = data;
    const users = allUsersResults;
    const employees = getEmployees(users);

    const initialReviewedUsers =
      initialPreset && !isArrayEmpty(users)
        ? users.filter(user =>
            isValueInList(initialPreset?.data?.subject, user.id)
          )
        : reviewedUsers;

    const initialReviewers =
      initialPreset && !isArrayEmpty(employees)
        ? employees.filter(user =>
            isValueInList(initialPreset?.data?.reviewer, user.id)
          )
        : reviewers;

    const initialPeriod = initialPreset
      ? presetPeriod(initialPreset?.data)
      : period;

    setReviewers(initialReviewers);
    setReviewedUsers(initialReviewedUsers);
    setSelectedSurveyType(
      initialPreset
        ? getSelectedSurveyType(
            surveyTypeOptions,
            initialPreset?.data?.survey_type
          )
        : INITIAL_SURVEY_TYPE_FILTER_VALUE
    );
    setPeriod(initialPeriod);
  };

  const getInitialData = useCallback(async () => {
    try {
      await Promise.all([
        getAttributesWithQuestions(dispatch, {
          with_questions: true,
          [PARAMS.EXCLUDE_ATTRIBUTE_TYPE]: ATTRIBUTE_TYPES_MAP.ENPS.id,
        }),
        getAllUsers(dispatch, {}, true),
        getJobTitles(dispatch),
        getTagsCategories(dispatch),
        getLevelsAndTracks(dispatch),
        getDivisionsAndDepartments(dispatch),
      ]).then(data => {
        handleInitData(data);
      });

      setIsLoading(false);
    } catch {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const onGetReviewedUsers = useCallback(data => {
    const { EXCLUDE_ROLE } = PARAMS;

    const params = {
      ...data,
      [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
    };

    return http.get(API_USERS_BASIC, {
      params,
      paramsSerializer: d => parseDuplicateParameters(d),
    });
  }, []);

  const onGetReviewers = useCallback(data => {
    const params = {
      ...data,
    };

    return http.get(API_USERS_BASIC, {
      params,
      paramsSerializer: d => parseDuplicateParameters(d),
    });
  }, []);

  const handleChange = useCallback(async () => {
    const isOpenEnded = initialAttribute?.text_reply_questions;
    const reviewersIds = getObjectToNumberArray(reviewers);
    let currentScore = null;
    let reviewFeedback = [];

    if (
      initialAttribute?.id &&
      (isSelfReport || !isArrayEmpty(reviewers)) &&
      (isPulseReport || !isArrayEmpty(reviewedUsers))
    ) {
      try {
        const [reportResults, currentReference, currentAttribute] =
          await Promise.all([
            http.get(
              isOpenEnded
                ? API_REPORT_ATTRIBUTE_REPLIES
                : API_REPORT_ATTRIBUTE_ANSWERS,
              {
                params: reportParams,
                paramsSerializer: d => parseDuplicateParameters(d),
              }
            ),
            http.get(API_REPORT_ATTRIBUTE_TIMELINE, {
              params: reportParams,
              paramsSerializer: d => parseDuplicateParameters(d),
            }),
            http.get(api_attribute(initialAttribute?.id)),
          ]);

        if (!isObjectEmpty(reportResults?.data)) {
          let currentAttributeScore = null;
          let currentFeedback = [];

          if (!isOpenEnded) {
            currentAttributeScore = await http.get(API_REPORT_ATTRIBUTE_SCORE, {
              params: reportParams,
              paramsSerializer: d => parseDuplicateParameters(d),
            });
          }

          if (currentAttribute?.data?.feedback_field) {
            currentFeedback = await http.get(API_REPORT_ATTRIBUTE_FEEDBACK, {
              params: reportParams,
              paramsSerializer: d => parseDuplicateParameters(d),
            });
          }
          currentScore = currentAttributeScore?.data?.score || null;
          reviewFeedback = currentFeedback?.data || [];
        }
        setAttributeScore(currentScore);
        setCurrentAttributeData(transformReviewData(currentAttribute?.data));
        setFeedback(reviewFeedback);
        setSurveyReference(currentReference?.data || []);
        setOpenEndedQuestionId(null);
        return setResults(
          isOpenEnded
            ? prepareTextReplyResults(reportResults?.data, reviewersIds)
            : prepareAnswerGridResults(reportResults?.data, reviewersIds)
        );
      } catch {
        setResults({});
        setAttributeScore(currentScore);
        setCurrentAttributeData(null);
        setFeedback(reviewFeedback);
        setSurveyReference([]);
        setOpenEndedQuestionId(null);
      }
    }
    setSurveyReference([]);
    setOpenEndedQuestionId(null);
    setAttributeScore(currentScore);
    setFeedback(reviewFeedback);

    return setResults({});
  }, [
    initialAttribute,
    reviewers,
    reviewedUsers,
    reportParams,
    isSelfReport,
    isPulseReport,
  ]);

  const cleanup = useCallback(() => {
    dispatch(clearAttributesWithQuestions());
    dispatch(clearAllUsers());
    dispatch(clearLevelsAndTracks());
    dispatch(clearDivisionsAndDepartments());
    dispatch(clearJobTitles());
    dispatch(clearTagsCategories());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (initialAttribute?.id) {
      handleChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleChange]);

  const handlePreviewOpenEndedQuestion = questionId => e => {
    e.stopPropagation();

    setOpenEndedQuestionId(prevQuestionId =>
      prevQuestionId !== questionId ? questionId : null
    );
  };

  const handleAttributeChange = selectedAttribute => {
    setAttribute(selectedAttribute);
    checkValueInPresets({ ...reportParams, attribute: selectedAttribute?.id });
  };

  const handlePeriodChange = newRange => {
    const { START, END } = PARAMS;
    setPeriod(newRange);
    checkValueInPresets({
      ...reportParams,
      from_date: getStartEndOfDayUtc(newRange[START]),
      to_date: getStartEndOfDayUtc(newRange[END], true),
    });
  };

  const handleSurveyTypeChange = selectedType => {
    setSelectedSurveyType(selectedType);
    const isSelf = areArraysContentsEqual(selectedType.value, [
      SURVEY_TYPES.SELF,
    ]);
    const isPulse = areArraysContentsEqual(selectedType.value, [
      SURVEY_TYPES.PULSE,
    ]);

    checkValueInPresets({
      ...reportParams,
      survey_type: selectedType.value,
      ...(isPulse ? {} : { subject: getObjectToNumberArray(reviewedUsers) }),
      ...(isSelf ? {} : { reviewer: getObjectToNumberArray(reviewers) }),
    });
  };

  const handleReviewedUsersChange = useCallback(
    selectedUsers => {
      setReviewedUsers(selectedUsers);
      checkValueInPresets({
        ...reportParams,
        subject: getObjectToNumberArray(selectedUsers),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportParams]
  );

  const handleReviewersUsersChange = useCallback(
    selectedUsers => {
      setReviewers(selectedUsers);
      checkValueInPresets({
        ...reportParams,
        reviewer: getObjectToNumberArray(selectedUsers),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportParams]
  );

  const handleToggleReadMore = () =>
    setIsReadMore(prevReadMore => !prevReadMore);

  const handlePlaceholder = name => {
    setDefaultPlaceholder(false);
    setPresetPlaceholderName(name);
  };

  const handleDeletePresetDialog = preset => {
    setSelectedPreset(preset);
    setIsDeletePresetDialogOpened(true);
  };

  const handleDeletePreset = () => {
    deleteSelectedPreset(dispatch, selectedPreset.id, ATTRIBUTE_PRESET_ID).then(
      () => {
        setIsDeletePresetDialogOpened(false);

        if (presets.active[ATTRIBUTE_PRESET_ID] === selectedPreset.id) {
          setSelectedPreset(undefined);
          setInitialPreset(undefined);
          setAlreadySavedMessage('');
        }
        showSuccessMessage(translations.presets.successMessages.deletedPreset);
      }
    );
  };

  const handleSavePreset = name => {
    const { START, END } = PARAMS;

    const data = {
      attribute: initialAttribute?.id,
      survey_type: selectedSurveyType.value,
      from_date: getStartEndOfDayUtc(period[START]),
      to_date: getStartEndOfDayUtc(period[END], true),
      subject: !isPulseReport ? getObjectToNumberArray(reviewedUsers) : [],
      reviewer: !isSelfReport ? getObjectToNumberArray(reviewers) : [],
    };
    addNewPreset(dispatch, {
      name,
      location: ATTRIBUTE_PRESET_ID,
      data,
    }).then(() => {
      setAlreadySavedMessage(translations.presets.used);
      showSuccessMessage(translations.presets.successMessages.createdPreset);
    });
  };

  const handleApplyPreset = preset => {
    const presetData = preset?.data;
    const presetAttribute = getItemById(
      attributesWithQuestions,
      presetData.attribute
    );
    const presetPeriodData = presetPeriod(presetData);
    const presetReviewedUsers = allEmployees.filter(user =>
      isValueInList(presetData?.subject, user.id)
    );
    const presetReviewers = allUsers.filter(user =>
      isValueInList(presetData?.reviewer, user.id)
    );

    setTempState(null);
    editPresetActivity(dispatch, preset.id, ATTRIBUTE_PRESET_ID, true);
    setSelectedPreset(preset);
    setInitialPreset(null);
    setAttribute(presetAttribute);
    setSelectedSurveyType(
      getSelectedSurveyType(surveyTypeOptions, presetData?.survey_type)
    );
    setPeriod(presetPeriodData);
    setReviewedUsers(presetReviewedUsers);
    setReviewers(presetReviewers);
  };

  const attributeDisabledPresetMessage = () => {
    const isPeerTeamDirectReport = !isSelfReport && !isPulseReport;
    const noReviewedUsers = !reviewedUsers || isArrayEmpty(reviewedUsers);
    const noReviewers = !reviewers || isArrayEmpty(reviewers);

    return !initialAttribute?.id ||
      (isSelfReport && noReviewedUsers) ||
      (isPulseReport && noReviewers) ||
      (isPeerTeamDirectReport && (noReviewedUsers || noReviewers))
      ? translations.presets.attributePresetConditionTooltip
      : '';
  };

  const presetSummary = useMemo(() => {
    const { START, END } = PARAMS;

    return {
      hasSurveyType: true,
      hasSingleAttribute: true,
      hasRange: true,
      hasSubjectList: !isPulseReport,
      hasReviewerList: !isSelfReport,
      translations: {
        surveyType: translations.selectFilters.surveyType,
        attribute: translations.selectFilters.attribute.label,
        range: translations.selectFilters.period,
        subject: translations.users.objectsOfReview.title,
        reviewers: translations.users.reviewers.title,
      },
      surveyTypes: selectedSurveyType?.label || '',
      attribute: initialAttribute?.id ? initialAttribute : null,
      range: {
        startDate: period[START],
        endDate: period[END],
      },
      subjectUsers: reviewedUsers,
      reviewerUsers: reviewers,
    };
  }, [
    initialAttribute,
    isPulseReport,
    isSelfReport,
    period,
    reviewedUsers,
    reviewers,
    selectedSurveyType,
    translations,
  ]);

  return (
    <PageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      navigate={navigate}
      shouldPassProps={false}
      isFullWidthContent
    >
      {!isLoading && (
        <div>
          <div className={classes.pageDescription}>
            <ReadLessMore
              translations={translations}
              isReadMore={isReadMore}
              toggleReadMore={handleToggleReadMore}
            >
              <Typography variant="body2">
                {translations.descriptionPartOne}
              </Typography>
              <Typography variant="body2">
                {translations.descriptionPartTwo}
              </Typography>
              <Typography variant="body2">
                {translations.descriptionPartThree}
              </Typography>
              <Typography variant="body2">
                {translations.descriptionPartFour}
              </Typography>
            </ReadLessMore>
          </div>
          <div className={classes.filtersWrapper}>
            <SelectField
              label={translations.selectFilters.surveyType}
              value={selectedSurveyType}
              options={surveyTypeOptions}
              onChange={handleSurveyTypeChange}
              shouldReturnOption
              isSearchDisabled
            />
            <SelectField
              label={translations.selectFilters.attribute.label}
              placeholder={translations.selectFilters.attribute.placeholder}
              value={initialAttribute}
              options={attributesWithQuestions}
              parser={{ value: 'id', label: 'name' }}
              onChange={handleAttributeChange}
              shouldReturnOption
              hasColorBox
              isAttribute
            />
            <CustomDateRange
              label={translations.selectFilters.period}
              initialRange={{
                startDate: period[PARAMS.START],
                endDate: period[PARAMS.END],
              }}
              startAtKey={PARAMS.START}
              endAtKey={PARAMS.END}
              onChange={handlePeriodChange}
            />
            <PresetDropdown
              translations={translations.presets}
              dropdownWidth={DROPDOWN_WIDTH}
              inputWidth={INPUT_WIDTH}
              options={filteredPresets}
              activePreset={activePresetId}
              handleApply={handleApplyPreset}
              handleDelete={handleDeletePresetDialog}
              handleSavePreset={handleSavePreset}
              disabledPreset={
                handleHasMaxPresets(filteredPresets, translations.presets) ||
                attributeDisabledPresetMessage()
              }
              alreadySavedMessage={alreadySavedMessage}
              presetPlaceholderName={placeholderName}
              presetSummary={presetSummary}
              handlePlaceholder={handlePlaceholder}
            />
          </div>
          <Fade in={!isPulseReport} appear={false} unmountOnExit>
            <div
              className={classNames(
                classes.filtersWrapper,
                classes.filtersUsersWrapper
              )}
            >
              <SectionTitle
                title={translations.users.objectsOfReview.title}
                variant="h4"
              />
              <Typography
                className={classes.usersSectionDescription}
                variant="body2"
              >
                {translations.users.objectsOfReview.description}
              </Typography>
              <MultipartPeoplePicker
                translations={translations.objectsPicker}
                filters={reviewedUsersFilters}
                allUsers={allEmployees}
                selectedUsers={reviewedUsers}
                onGetUsers={onGetReviewedUsers}
                onSave={handleReviewedUsersChange}
              />
            </div>
          </Fade>
          <Fade in={!isSelfReport} appear={false} unmountOnExit>
            <div
              className={classNames(
                classes.filtersWrapper,
                classes.filtersUsersWrapper
              )}
            >
              <SectionTitle
                title={translations.users.reviewers.title}
                variant="h4"
              />
              <Typography
                className={classes.usersSectionDescription}
                variant="body2"
              >
                {translations.users.reviewers.description}
              </Typography>
              <MultipartPeoplePicker
                translations={translations.reviewersPicker}
                filters={reviewersFilters}
                allUsers={allUsers}
                selectedUsers={reviewers}
                onGetUsers={onGetReviewers}
                onSave={handleReviewersUsersChange}
              />
            </div>
          </Fade>
          {currentAttributeData && !isObjectEmpty(results) && (
            <AttributeInfoBar
              className={classes.topSpacing}
              translations={translations.attributeInfoBar}
              attribute={currentAttributeData}
              averageScore={attributeScore}
              shouldDisableIndividualScores
              shouldShowScores
            />
          )}
          <DeepDiveReport
            translations={translations.review}
            results={results}
            feedback={feedback}
            attribute={currentAttributeData}
            openEndedQuestionId={openEndedQuestionId}
            isOpenEndedAttribute={isOpenEndedAttribute}
            canAccessUserProfileCheck={canAccessUserProfileCheck}
            onOpenEndedQuestionPreview={handlePreviewOpenEndedQuestion}
            onGoToUserProfile={goToUserProfilePage}
          />
          <NotificationCard
            className={classes.topSpacing}
            shouldFade={isObjectEmpty(results)}
            content={translations.noResults}
          />
          {!isArrayEmpty(surveyReference) && (
            <ReportDetailsTable
              className={classes.topSpacing}
              translations={translations.review.details}
              columns={detailsTableColumns}
              statuses={surveyStatuses}
              details={surveyReference}
              hasRedirectLinkCheck={hasRedirectLinkCheck}
              prepareRedirectLinkPath={prepareSurveyLinkPath}
              isTimeline
            />
          )}
          <AlertDialog
            translations={translations.presets.presetDeleteDialog}
            isOpened={isDeletePresetDialogOpened}
            onClose={() => setIsDeletePresetDialogOpened(false)}
            onConfirm={handleDeletePreset}
            isWarning
          />
        </div>
      )}
    </PageContainer>
  );
};

AttributeReportPage.propTypes = {
  navigate: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
};

export default memo(AttributeReportPage);
